import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "@/lib/cn";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "destructive"
  | "destructiveGhost"
  | "pop"
  | "indigo";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Render as the passed child element instead of a `<button>`. Useful for
   * wrapping anchors or react-router `<Link>` without nesting interactive
   * elements. Mirrors the shadcn/radix `asChild` convention.
   */
  asChild?: boolean;
  /**
   * When true, prepend a leading spinner and disable the button. Label is
   * preserved so layout stays stable across the async transition. `aria-busy`
   * set to `true` via props has the same effect — either trigger is accepted
   * so callers that already annotate busy state don't need a second prop.
   */
  loading?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  // Primary: ink-on-paper "workhorse" — solid near-black with white text.
  // Flat by default; tiny press translation on active.
  primary:
    "bg-[var(--ink)] text-white border border-[var(--ink)] hover:bg-[var(--ink-2)] hover:border-[var(--ink-2)] active:translate-y-[0.5px]",
  // Secondary: paper surface with hairline border. Low-emphasis next to primary.
  secondary:
    "bg-[var(--surface)] text-[var(--ink)] border border-[var(--line-2)] hover:bg-[var(--surface-2)]",
  // Ghost: zero-chrome, surface tint on hover. Used in toolbars.
  ghost:
    "bg-transparent text-[var(--ink)] border border-transparent hover:bg-[var(--surface-2)]",
  // Destructive: red filled button.
  destructive:
    "bg-[var(--neg)] text-white border border-[var(--neg)] hover:brightness-95 active:brightness-90",
  // Destructive-ghost: low-emphasis dangerous action. Kept on `text-negative`
  // / `hover:bg-[var(--neg-bg)]` Tailwind utilities so existing tests still pass;
  // legacy aliases resolve them to v2 red.
  destructiveGhost:
    "bg-transparent text-negative border border-transparent hover:bg-[var(--neg-bg)]",
  // Pop: chartreuse marketing/auth CTA — used on /login, /register submits.
  pop:
    "bg-[var(--chart)] text-[var(--chart-ink)] border border-[var(--chart-2)] hover:bg-[var(--chart-2)] hover:border-[var(--chart-2)]",
  // Indigo: app accent emphasis — when you specifically want the brand
  // accent on a button instead of ink.
  indigo:
    "bg-[var(--indigo)] text-white border border-[var(--indigo)] hover:bg-[var(--indigo-2)] hover:border-[var(--indigo-2)]",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-7 px-2.5 text-xs gap-1.5",
  md: "h-9 px-3.5 text-[13px] gap-2",
  lg: "h-11 px-5 text-sm gap-2",
};

const baseClasses = [
  "inline-flex items-center justify-center whitespace-nowrap",
  // v2 label: Inter 500 with very light tracking.
  "font-sans font-medium tracking-[0.01em] rounded-[var(--r-md)] select-none",
  "transition-[background-color,border-color,filter,transform] duration-fast ease",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--indigo)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)]",
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none",
].join(" ");

/**
 * Tiny current-color spinner. Exposed as a standalone export so pages can
 * render it outside of buttons — inside table cells during row-level async
 * actions, inside toasts, etc. — without reimplementing the class soup.
 */
export function LoadingSpinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn(
        "inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent",
        className,
      )}
    />
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant = "primary",
      size = "md",
      asChild = false,
      type,
      loading = false,
      disabled,
      "aria-busy": ariaBusyProp,
      children,
      ...props
    },
    ref,
  ) {
    const Component = asChild ? Slot : "button";
    // Only set a default `type` when rendering an actual <button> — Slot may
    // wrap an <a> or similar where `type` has a different meaning.
    const buttonType = asChild ? undefined : type ?? "button";

    const isBusy = loading || ariaBusyProp === true || ariaBusyProp === "true";
    // Disable when explicitly disabled OR when busy — busy buttons should
    // swallow clicks so users can't double-submit during an in-flight action.
    const isDisabled = disabled || isBusy;

    // `Slot` forwards a single child, so when rendering asChild we must not
    // inject a spinner sibling — doing so would violate Slot's contract and
    // fight react-router `<Link>` wrapping. Spinner is button-only.
    //
    // When busy, wrap `children` in an `opacity-0` span (still in the a11y
    // tree so the button's name stays derived from the label — e.g. "Save")
    // and absolutely-position the spinner over it. That way:
    //  - The button's intrinsic width is preserved (no reflow when an async
    //    action completes), and
    //  - Icon-only buttons don't render `[spinner][icon]` side by side —
    //    the icon is painted underneath the spinner which spins over it.
    //
    // Using `opacity-0` (not `invisible`/`display:none`) is important for
    // accessibility: `visibility: hidden` strips descendants from the a11y
    // tree, which would collapse the button's accessible name to whatever
    // the spinner contributes instead of the real label.
    const content =
      isBusy && !asChild ? (
        <span className="relative inline-flex items-center justify-center gap-[inherit]">
          <span className="pointer-events-none inline-flex items-center gap-[inherit] opacity-0">
            {children}
          </span>
          <span className="absolute inset-0 inline-flex items-center justify-center">
            <LoadingSpinner />
          </span>
        </span>
      ) : (
        children
      );

    return (
      <Component
        ref={ref}
        type={buttonType}
        aria-busy={isBusy || undefined}
        disabled={isDisabled}
        className={cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {content}
      </Component>
    );
  },
);
